import { useState, useEffect } from 'react';
import { palette } from '../../styles/tokens';
import { useCreateBooking } from '../../hooks/useCreateBooking';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { MoneyInput } from '../common/MoneyInput';
import { ClientPicker, type ClientSelection } from '../common/ClientPicker';
import { DatePicker } from '../common/DatePicker';
import { TimePicker } from '../common/TimePicker';
import { canonicalMoney } from '../../lib/money';

interface Props { onCreated: () => void; onCancel: () => void; }
interface TeamUser { id: string; first_name: string; last_name: string; }

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

function addMinutes(time: string, mins: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function CreateBookingForm({ onCreated, onCancel }: Props) {
  const business = useAuthStore(s => s.business);
  const { createBooking, services, loading, error } = useCreateBooking(business?.id);
  const [users, setUsers] = useState<TeamUser[]>([]);

  const [serviceId, setServiceId] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  // Rule #33: full ClientSelection captured; bookings persists client_id +
  // denormalized client_name/client_phone synced from selection.
  const [clientSelection, setClientSelection] = useState<ClientSelection | null>(null);
  const [assignedId, setAssignedId] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!business) return;
    supabase.from('users').select('id, first_name, last_name').eq('business_id', business.id).eq('status', 'active')
      .then(({ data }) => setUsers(data ?? []));
  }, [business]);

  // Auto-fill duration + price from service
  const handleServiceChange = (id: string) => {
    setServiceId(id);
    const svc = services.find(s => s.id === id);
    if (svc) {
      setEndTime(addMinutes(startTime, svc.duration_minutes));
      setPaymentAmount(canonicalMoney(svc.price));
    }
  };

  const user = useAuthStore(s => s.user);

  const handleSubmit = async () => {
    if (!business || !user) return;
    const svc = services.find(s => s.id === serviceId);
    const ok = await createBooking({
      business_id: business.id, created_by: user.id,
      service_id: serviceId || undefined, service_name: svc?.name,
      client_id: clientId ?? undefined,
      client_name: clientSelection?.name ?? '',
      client_phone: clientSelection?.phone ?? '',
      assigned_id: assignedId || undefined, date, start_time: startTime, end_time: endTime,
      // Q1.5 boundary: hook accepts number; paymentAmount is canonical after MoneyInput blur.
      payment_amount: paymentAmount ? Number(paymentAmount) : undefined,
      booked_price: svc?.price ?? null,
      notes,
    });
    if (ok) onCreated();
  };

  return (
    <div>
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}
      {services.length > 0 && (
        <div style={FIELD}><label style={LABEL}>Service</label><select style={INPUT} value={serviceId} onChange={e => handleServiceChange(e.target.value)}>
          <option value="">Select service...</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes}min — ${s.price})</option>)}
        </select></div>
      )}
      <div style={FIELD}><label style={LABEL}>Client *</label>
        <ClientPicker
          value={clientId}
          onChange={(sel) => { setClientId(sel?.clientId ?? null); setClientSelection(sel); }}
        />
      </div>
      <div style={FIELD}><label style={LABEL}>Assigned to</label><select style={INPUT} value={assignedId} onChange={e => setAssignedId(e.target.value)}><option value="">Unassigned</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select></div>
      <div style={FIELD}><label style={LABEL}>Date *</label>
        <DatePicker value={date || null} onChange={(v) => setDate(v ?? '')} minDate={new Date().toISOString().split('T')[0]} />
      </div>
      <div style={{ display: 'flex', gap: 12, ...FIELD }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Start *</label>
          <TimePicker value={startTime || null} onChange={(v) => setStartTime(v ?? '')} step={15} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>End *</label>
          <TimePicker value={endTime || null} onChange={(v) => setEndTime(v ?? '')} step={15} minTime={startTime || undefined} />
        </div>
      </div>
      <div style={FIELD}><label style={LABEL}>Amount</label><MoneyInput value={paymentAmount} onChange={setPaymentAmount} /></div>
      <div style={FIELD}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading || !serviceId} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: loading || !serviceId ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: loading || !serviceId ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{loading ? 'Creating...' : 'Create Booking'}</button>
        </div>
        {!serviceId && !loading && (
          <p style={{ fontSize: 11, color: palette.g400, marginTop: 6, marginBottom: 0 }}>Pick a service to continue</p>
        )}
      </div>
    </div>
  );
}
